import { Box, CardContent, Divider, Stack } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import { AWSSubnetSelect } from '@/components/selectors/awsSubnetSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { VpcSelect } from '@/components/selectors/vpcSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { EonTagRemark } from './eonTagRemark';
import { StartInstanceAfterRestoreToggle } from './startInstanceAfterRestoreToggle';

import { StepContainer } from '../../wizard/StepContainer';
import { InstanceTypeSelect } from '../instanceTypeSelection';
import type { RestoreEc2InstanceState } from '../restoreEc2InstanceWizard';

export const InstanceConfiguration = (
  props: StepProps<RestoreEc2InstanceState>
) => {
  const dal = useDAL();
  const { Ec2EnableRestoringStoppedInstance } = useFeatureFlags();

  const { data: subnets } = dal.restore.awsSubnets.useList(
    props.wizardState.restoreAccount?.id || '',
    props.wizardState.regionName
  );

  useEffect(() => {
    if (subnets?.subnets) {
      props.setWizardState((state) => {
        const subnet = subnets.subnets.find(
          (subnet) => subnet.id === props.wizardState.AWSSubnetId
        );
        return {
          ...state,
          vpc: subnet?.vpc || undefined,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subnets]);

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={<EonTagRemark />}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      nextButtonText='Next'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          !props.wizardState.regionName ||
          !props.wizardState.instanceType ||
          !props.wizardState.AWSSubnetId
        ) {
          return false;
        }

        return true;
      }}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='my-[24px] mx-[40px]'>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <RestoreRegionSelect
            isRequired
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(regionName) =>
              props.setWizardState((state) => ({
                ...state,
                regionName: regionName,
              }))
            }
          />
          <VpcSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            vpc={props.wizardState.vpc}
            onChange={(vpc) =>
              props.setWizardState((state) => ({
                ...state,
                vpc: vpc,
              }))
            }
            disabledItems={[]}
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <AWSSubnetSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            subnetId={props.wizardState.AWSSubnetId}
            vpcScope={props.wizardState.vpc || ''}
            onChange={(AWSSubnetId) =>
              props.setWizardState((state) => ({
                ...state,
                AWSSubnetId,
              }))
            }
          />
          <InstanceTypeSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            subnetId={
              props.wizardState.vpc ? props.wizardState.AWSSubnetId : undefined
            }
            instanceType={props.wizardState.instanceType}
            onChange={(instanceType) =>
              props.setWizardState((state) => ({
                ...state,
                instanceType: instanceType,
              }))
            }
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <SecurityGroupSelect
            isOptionalField={true}
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            securityGroupIds={props.wizardState.securityGroupIds}
            vpc={props.wizardState.vpc || ''}
            subnetId={props.wizardState.AWSSubnetId}
            isSubnetIdMandatory={true}
            onChange={(securityGroupIds) =>
              props.setWizardState((state) => ({
                ...state,
                securityGroupIds: securityGroupIds,
              }))
            }
          />
          <div className='flex-1' />
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.initialState.tags || {}}
          tags={props.wizardState.tags || {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({
              ...state,
              tags: tags,
            }))
          }
        />
      </CardContent>
      {Ec2EnableRestoringStoppedInstance && (
        <>
          <Divider />
          <StartInstanceAfterRestoreToggle
            checked={props.wizardState.startInstanceAfterRestore ?? true}
            onChange={() => {
              props.setWizardState((state) => ({
                ...state,
                startInstanceAfterRestore: !state.startInstanceAfterRestore,
              }));
            }}
          />
        </>
      )}
    </StepContainer>
  );
};
